import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Package, Clock, XCircle, ArrowLeft, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const OrderStatus = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/api/instructor-order-status/${orderId}`);
        setOrder(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching order:', err);
        setError(err.response?.data?.detail || 'Order not found');
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrder();
    }
  }, [orderId]);

  const getStatusConfig = (status) => {
    switch (status) {
      case 'pending':
        return {
          icon: Clock,
          color: 'text-amber-400',
          bgColor: 'bg-amber-900/30',
          borderColor: 'border-amber-600',
          label: 'Pending'
        };
      case 'ordered':
        return {
          icon: Package,
          color: 'text-blue-400',
          bgColor: 'bg-blue-900/30',
          borderColor: 'border-blue-600',
          label: 'Ordered'
        };
      case 'fulfilled':
        return {
          icon: CheckCircle,
          color: 'text-emerald-400',
          bgColor: 'bg-emerald-900/30',
          borderColor: 'border-emerald-600',
          label: 'Complete'
        };
      case 'cancelled':
        return {
          icon: XCircle,
          color: 'text-red-400',
          bgColor: 'bg-red-900/30',
          borderColor: 'border-red-600',
          label: 'Cancelled'
        };
      default:
        return {
          icon: Clock,
          color: 'text-slate-400',
          bgColor: 'bg-slate-900/30',
          borderColor: 'border-slate-600',
          label: 'Unknown'
        };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-teal-400 mx-auto" />
          <p className="mt-4 text-slate-400">Loading order status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-800 rounded-xl p-8 text-center border border-slate-700">
          <XCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-100 mb-2">Order Not Found</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(order?.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="min-h-screen bg-slate-900 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-100 mb-2">Order Status</h1>
          <p className="text-slate-400">Track your eCard order</p>
        </div>

        {/* Order Card */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          {/* Status Header */}
          <div className={`${statusConfig.bgColor} border-b ${statusConfig.borderColor} p-6`}>
            <div className="flex items-center justify-center gap-3">
              <StatusIcon className={`h-8 w-8 ${statusConfig.color}`} />
              <span className={`text-2xl font-bold ${statusConfig.color}`}>
                {statusConfig.label}
              </span>
            </div>
            <p className="text-center mt-2 text-slate-300">
              {order?.status_message}
            </p>
          </div>

          {/* Order Details */}
          <div className="p-6 space-y-6">
            {/* Order ID */}
            <div className="bg-slate-700/50 rounded-lg p-4">
              <p className="text-sm text-slate-400 mb-1">Order Number</p>
              <p className="font-mono text-lg text-slate-100">{order?.order_id}</p>
            </div>

            {/* Customer Info */}
            <div className="bg-slate-700/50 rounded-lg p-4">
              <p className="text-sm text-slate-400 mb-1">Ordered By</p>
              <p className="text-slate-100">{order?.instructor_name}</p>
            </div>

            {/* Timeline */}
            <div className="space-y-3">
              <h3 className="text-slate-200 font-medium">Order Timeline</h3>
              <div className="space-y-2">
                {order?.created_at && (
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-2 h-2 rounded-full bg-teal-400"></div>
                    <span className="text-slate-400">Order Placed:</span>
                    <span className="text-slate-200">{formatDate(order.created_at)}</span>
                  </div>
                )}
                {order?.ordered_at && (
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                    <span className="text-slate-400">Cards Ordered:</span>
                    <span className="text-slate-200">{formatDate(order.ordered_at)}</span>
                  </div>
                )}
                {order?.fulfilled_at && (
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                    <span className="text-slate-400">Order Complete:</span>
                    <span className="text-slate-200">{formatDate(order.fulfilled_at)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Items */}
            <div>
              <h3 className="text-slate-200 font-medium mb-3">Order Items</h3>
              <div className="bg-slate-700/50 rounded-lg divide-y divide-slate-600">
                {order?.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center p-4">
                    <span className="text-slate-200">{item.product_name}</span>
                    <span className="text-slate-400">x{item.quantity}</span>
                  </div>
                ))}
              </div>
              {order?.total && (
                <div className="mt-3 text-right">
                  <span className="text-slate-400">Total: </span>
                  <span className="text-xl font-bold text-teal-400">${order.total.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-slate-700/30 px-6 py-4 border-t border-slate-700">
            <p className="text-center text-slate-400 text-sm">
              Questions about your order? Contact us at{' '}
              <a href="mailto:support@biggestlittlecpr.com" className="text-teal-400 hover:text-teal-300">
                support@biggestlittlecpr.com
              </a>
            </p>
          </div>
        </div>

        {/* Back Link */}
        <div className="text-center mt-8">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderStatus;
